import React, { forwardRef, useEffect, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { useQuery, gql } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import Loader from '../../../../../components/Loader';
// @ts-expect-error
import CmsContent from '../CmsContent';
import AssessmentContent from '../AssessmentContent';
import { trackAction } from '../../../../../helpers/trackAction';
import JsonCmsContent from '../../../../content/JsonCmsContent';
import { User } from '../../../../../context/AuthProvider';
import { Content, StudyGroup } from '../../../../../generated/graphqlModels';
import { State } from '../hooks/useGetUnitDataGql';

const timestamp = new Date().getTime();

export const GET_CONTENT = gql`
  query Content($contentId: ID!) {
    content(pk: $contentId) {
      id
      pk
      name
      mediaType
      pdfUrl
      youtubeUrl
      preziUrl
      driveUrl
      fileUrl
      isDownloadable
      isDemo
      isPublished
      isShareable
    }
  }
`;

interface ContentProps {
  contentId: string;
  context: User;
  isChalkedUp: boolean;
  isChalkupsLoading: boolean;
  studyGroup?: StudyGroup;
  toggleComplete: () => void;
  goToNextResource: () => void;
  isTeacherGuideDisplayed: boolean;
  updateParentState: (newState: Partial<State>) => void;
  shouldGoNext: number;
  setShouldGoNext: React.Dispatch<React.SetStateAction<number>>;
  popup: Window | null;
  setPopup: React.Dispatch<React.SetStateAction<Window | null>>;
  presentationEnabled: boolean;
  setPresentationEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  showAllSections?: boolean;
  isExplore?: boolean;
  isProgramUsingNewContentRendering: boolean;
  isUsingMaxWidth?: boolean;
  isProgramContentRenderingLoading: boolean;
}

const ContentComponent = forwardRef<HTMLIFrameElement, ContentProps>(
  (props, iframeRef) => {
    const {
      contentId,
      context,
      studyGroup,
      isChalkedUp,
      isChalkupsLoading,
      toggleComplete,
      goToNextResource,
      isTeacherGuideDisplayed,
      updateParentState,
      shouldGoNext,
      setShouldGoNext,
      popup,
      setPopup,
      presentationEnabled,
      setPresentationEnabled,
      showAllSections,
      isExplore,
      isProgramUsingNewContentRendering,
      isUsingMaxWidth,
      isProgramContentRenderingLoading,
    } = props;
    const { t } = useTranslation();

    const {
      data: contentData,
      loading: isLoadingContent,
      error: contentError,
    } = useQuery(GET_CONTENT, { variables: { contentId } });

    const [content, setContent] = useState<Content>();

    useEffect(() => {
      if (contentData && contentData.content) {
        setContent(contentData.content);
      }
    }, [contentData]);

    useEffect(() => {
      updateParentState({ contentName: content?.name });
    }, [content]);

    const [isLoading, setIsLoading] = useState(false);
    useEffect(() => {
      if (content) {
        switch (content.mediaType) {
          case 'pdf':
            setIsLoading(true);
            break;
          case 'cms':
            break;
          case 'file':
            break;
          case 'drive':
            break;
          case 'assessment':
            break;
          default:
            setIsLoading(true);
        }
      }
    }, [content]);

    const contentOpened = () => {
      if (content && studyGroup) {
        trackAction(
          {
            action_type: 'opened',
            action_object: { type: 'content', pk: content.pk },
            study_group: { pk: studyGroup.pk },
          },
          context.user_actions,
        );
      }
    };

    const onIframeLoad = () => {
      setIsLoading(false);
      contentOpened();
    };

    useEffect(() => {
      if (content && ['cms', 'file'].includes(content.mediaType)) {
        contentOpened();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [content]);

    if (content == null) {
      return null;
    }

    return (
      <Box
        sx={{
          '.sidebar-opened &': {
            width: '100%',
            left: 0,
          },
        }}
        transition="500ms"
        data-cy-content
        w="full"
        pr="10px"
        position="relative"
      >
        {content.mediaType === 'pdf' ? (
          <iframe
            title="pdf"
            ref={iframeRef}
            src={
              '/static/ui/pdfjs/web/viewer.html?file=' +
              content.pdfUrl +
              '&timestamp=' +
              timestamp
            }
            frameBorder="0"
            allowFullScreen
            width="100%"
            height="100vh"
            style={{ height: '100vh' }}
            onLoad={() => onIframeLoad()}
          />
        ) : content.mediaType === 'cms' ? (
          isProgramContentRenderingLoading ? null : isProgramUsingNewContentRendering ? (
            <JsonCmsContent
              ref={iframeRef}
              content={content}
              isChalkedUp={isChalkedUp}
              isChalkupsLoading={isChalkupsLoading}
              isTeacherGuideDisplayed={isTeacherGuideDisplayed}
              toggleComplete={toggleComplete}
              // @ts-ignore
              studyGroup={studyGroup}
              goToNextResource={goToNextResource}
              popup={popup}
              setPopup={setPopup}
              shouldGoNext={shouldGoNext}
              setShouldGoNext={setShouldGoNext}
              presentationEnabled={presentationEnabled}
              setPresentationEnabled={setPresentationEnabled}
              showAllSections={showAllSections}
              isUsingMaxWidth={isUsingMaxWidth}
            />
          ) : (
            <CmsContent
              ref={iframeRef}
              content={content}
              isChalkedUp={isChalkedUp}
              isChalkupsLoading={isChalkupsLoading}
              isTeacherGuideDisplayed={isTeacherGuideDisplayed}
              toggleComplete={toggleComplete}
              studyGroup={studyGroup}
              goToNextResource={goToNextResource}
              showAllSections={showAllSections}
              isUsingMaxWidth={isUsingMaxWidth}
            />
          )
        ) : content.mediaType === 'file' ? (
          <div style={{ textAlign: 'left', paddingLeft: '10px' }}>
            <p>{t('content.toAccess', { contentName: content.name })}</p>
            <ul>
              <li>{content.fileUrl?.substr('/media/'.length)}</li>
            </ul>
          </div>
        ) : content.mediaType === 'drive' ? (
          <iframe
            ref={iframeRef}
            title="Drive"
            src={content.driveUrl || ''}
            width="100%"
            height="100vh"
            style={{ height: '100vh' }}
            frameBorder="0"
            onLoad={() => onIframeLoad()}
          >
            Loading...
          </iframe>
        ) : content.mediaType === 'assessment' ? (
          <AssessmentContent
            ref={iframeRef}
            content={content}
            // @ts-ignore
            studyGroup={studyGroup}
            setIsSaveAvailable={(isSaveAvailable) => {
              updateParentState({ isSaveAvailable });
            }}
            setIsSaving={(isSaving) => {
              updateParentState({ isSaving });
            }}
            onLoad={() => onIframeLoad()}
          />
        ) : (
          <iframe
            title="Media"
            ref={iframeRef}
            // @ts-ignore
            src={content[`${content.mediaType}Url`]}
            frameBorder="0"
            allowFullScreen
            onLoad={() => onIframeLoad()}
          />
        )}
        {isLoading && <Loader />}
      </Box>
    );
  },
);

export default ContentComponent;
